import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';

import { useFeatureFlagEnabled, useFeatureFlagPayload, usePostHog } from 'posthog-js/react';

import type { FeatureFlagPayloadMap, FlagKey, PayloadFlagKey } from './index';

export function useFlag<K extends FlagKey>(key: K): boolean {
  return Boolean(useFeatureFlagEnabled(key));
}

export function useFlagPayload<K extends PayloadFlagKey>(
  key: K
): FeatureFlagPayloadMap[K] | undefined {
  return useFeatureFlagPayload(key) as FeatureFlagPayloadMap[K] | undefined;
}

export function useFeatureFlagsLoaded(): boolean {
  const posthog = usePostHog();
  const [loaded, setLoaded] = useState(() => posthog.featureFlags.hasLoadedFlags);

  useEffect(() => {
    if (loaded) return;
    return posthog.onFeatureFlags(() => {
      setLoaded(true);
    });
  }, [posthog, loaded]);

  return loaded;
}

export function withFeatureFlag<P extends object>(
  flag: FlagKey,
  Component: ComponentType<P>
): ComponentType<P> {
  return function FeatureFlagGate(props: P) {
    const isEnabled = useFlag(flag);
    if (!isEnabled) return null;
    return <Component {...props} />;
  };
}
